import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { puedeEditar } from '../../domain/permissions';
import AcademiaConfig from './AcademiaConfig';
import {
  cambiarEstadoCurso,
  crearCurso,
  listarCursos,
  listarEspacios,
  listarResultados,
  obtenerDetalleRecurso,
  type CursoRow,
  type EspacioRow,
  type EstadoCurso,
  type ResultadoRecurso,
  type CursoDelRecurso,
} from '../../api/academia';

const ESTADO_ETIQUETA: Record<EstadoCurso, string> = {
  borrador: 'Borrador',
  publicado: 'Publicado',
  proximamente: 'Próximamente',
  archivado: 'Archivado',
};

const ESTADO_COLOR: Record<EstadoCurso, string> = {
  borrador: 'bg-gray-100 text-gray-700',
  publicado: 'bg-green-100 text-green-800',
  proximamente: 'bg-amber-100 text-amber-800',
  archivado: 'bg-gray-100 text-gray-400',
};

/** Título → slug. Los ids son uuid; el slug es la clave legible dentro del espacio. */
function aSlug(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export default function Academia() {
  const { roles } = useAuth();
  const puedeCargar = puedeEditar('academia', roles);

  const [espacios, setEspacios] = useState<EspacioRow[]>([]);
  const [espacioId, setEspacioId] = useState('');
  const [cursos, setCursos] = useState<CursoRow[]>([]);
  const [resultados, setResultados] = useState<ResultadoRecurso[]>([]);
  const [pestania, setPestania] = useState<'cursos' | 'resultados' | 'config'>('cursos');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [tituloNuevo, setTituloNuevo] = useState('');
  const [filtro, setFiltro] = useState<'todos' | 'pendientes' | 'completos'>('todos');

  useEffect(() => {
    let vivo = true;
    listarEspacios()
      .then((e) => {
        if (!vivo) return;
        setEspacios(e);
        setEspacioId((actual) => actual || e[0]?.id || '');
      })
      .catch((err) => vivo && setError(mensaje(err)))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, []);

  const recargar = useCallback(async () => {
    if (!espacioId) return;
    try {
      setCursos(await listarCursos(espacioId));
    } catch (err) {
      setError(mensaje(err));
    }
  }, [espacioId]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  useEffect(() => {
    if (pestania !== 'resultados' || !espacioId) return;
    listarResultados(espacioId)
      .then(setResultados)
      .catch((err) => setError(mensaje(err)));
  }, [pestania, espacioId]);

  async function alCrear() {
    const titulo = tituloNuevo.trim();
    if (!titulo || !espacioId) return;
    setCreando(true);
    setError(null);
    try {
      await crearCurso(espacioId, {
        titulo,
        slug: aSlug(titulo),
        descripcion: '',
        emoji: '📘',
        duracionMin: 15,
      });
      setTituloNuevo('');
      await recargar();
    } catch (err) {
      setError(mensaje(err));
    } finally {
      // Siempre: si no, el botón queda muerto tras el primer error.
      setCreando(false);
    }
  }

  async function alCambiarEstado(curso: CursoRow, estado: EstadoCurso) {
    setError(null);
    try {
      await cambiarEstadoCurso(curso.id, estado);
      await recargar();
    } catch (err) {
      setError(mensaje(err));
    }
  }

  if (cargando) return <div className="p-8 text-center text-gray-500">Cargando…</div>;

  if (espacios.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="font-semibold">Todavía no hay ningún espacio de Academia</p>
        <p className="mt-1 text-sm text-gray-500">
          Un espacio es «la Academia de un cliente»: su nombre, su logo y sus colores. Se crea
          desde la base mientras no exista la pantalla.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Academia</h1>
          <p className="text-sm text-gray-500">
            Contenido de capacitación y resultados por recurso
          </p>
        </div>

        {espacios.length > 1 && (
          <select
            value={espacioId}
            onChange={(e) => setEspacioId(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            {espacios.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}
              </option>
            ))}
          </select>
        )}
      </header>

      <nav className="mb-4 flex gap-2 border-b">
        {(['cursos', 'resultados', 'config'] as const).map((p) => (
          <button
            key={p}
            onClick={() => setPestania(p)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
              pestania === p
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {p === 'cursos' ? 'Cursos' : p === 'resultados' ? 'Resultados' : 'Configuración'}
          </button>
        ))}
      </nav>

      {error && (
        <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {pestania === 'config' ? (
        <AcademiaConfig espacioId={espacioId} editable={puedeCargar} />
      ) : pestania === 'cursos' ? (
        <>
          {puedeCargar && (
            <div className="mb-4 flex flex-wrap gap-2 rounded-lg border bg-gray-50 p-3">
              <input
                value={tituloNuevo}
                onChange={(e) => setTituloNuevo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void alCrear()}
                placeholder="Título del curso nuevo"
                className="min-w-[240px] flex-1 rounded-lg border px-3 py-2 text-sm"
              />
              <button
                onClick={() => void alCrear()}
                disabled={creando || !tituloNuevo.trim()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {creando ? 'Creando…' : 'Crear curso'}
              </button>
              <p className="w-full text-xs text-gray-500">
                Nace en <b>borrador</b>: nadie lo ve hasta que lo publiques.
              </p>
            </div>
          )}

          <div className="space-y-2">
            {cursos.length === 0 ? (
              <p className="py-8 text-center text-gray-500">
                Este espacio todavía no tiene cursos.
              </p>
            ) : (
              cursos.map((c) => (
                <article
                  key={c.id}
                  className="flex flex-wrap items-center gap-3 rounded-lg border bg-white p-3"
                >
                  <span className="text-2xl">{c.emoji}</span>

                  <div className="min-w-[200px] flex-1">
                    <h2 className="font-semibold">{c.titulo}</h2>
                    <p className="text-xs text-gray-500">
                      {c.duracion_min} min · nota mínima {c.nota_minima} · orden {c.orden}
                    </p>
                  </div>

                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${ESTADO_COLOR[c.estado]}`}
                  >
                    {ESTADO_ETIQUETA[c.estado]}
                  </span>

                  {puedeCargar && (
                    <>
                      <select
                        value={c.estado}
                        onChange={(e) => void alCambiarEstado(c, e.target.value as EstadoCurso)}
                        className="rounded-lg border px-2 py-1.5 text-xs"
                        aria-label={`Estado de ${c.titulo}`}
                      >
                        {(Object.keys(ESTADO_ETIQUETA) as EstadoCurso[]).map((e) => (
                          <option key={e} value={e}>
                            {ESTADO_ETIQUETA[e]}
                          </option>
                        ))}
                      </select>
                      <Link
                        to={`/panel/academia/curso/${c.id}`}
                        className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        Editar contenido
                      </Link>
                    </>
                  )}
                </article>
              ))
            )}
          </div>
        </>
      ) : (
        <ResultadosTabla
          espacioId={espacioId}
          filas={resultados}
          filtro={filtro}
          onFiltro={setFiltro}
        />
      )}
    </div>
  );
}

/**
 * La tabla contesta «¿a quién hay que ir a buscar?», no «¿quién avanzó?».
 *
 * Por eso lista a TODOS los que tienen acceso —hayan hecho algo o no— y ordena por quién
 * más debe. Antes salía solo quien ya había dejado rastro, y los que nunca entraron
 * simplemente no existían para la pantalla: justo la gente que hay que perseguir.
 */
function ResultadosTabla({
  espacioId,
  filas,
  filtro,
  onFiltro,
}: {
  espacioId: string;
  filas: ResultadoRecurso[];
  filtro: 'todos' | 'pendientes' | 'completos';
  onFiltro: (f: 'todos' | 'pendientes' | 'completos') => void;
}) {
  /** Qué fila está abierta. Solo una: dos detalles abiertos no se comparan, se estorban. */
  const [abierta, setAbierta] = useState<string | null>(null);
  const pendientes = filas.filter((f) => f.sin_empezar > 0 || f.en_curso > 0);
  const completos = filas.filter((f) => f.sin_empezar === 0 && f.en_curso === 0);
  const visibles =
    filtro === 'pendientes' ? pendientes : filtro === 'completos' ? completos : filas;

  const sinEmpezarNunca = filas.filter(
    (f) => f.sin_empezar === f.cursos_publicados && f.cursos_publicados > 0,
  ).length;

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Tarjeta valor={filas.length} etiqueta="Con acceso" />
        <Tarjeta
          valor={completos.length}
          etiqueta="Completaron todo"
          tono="text-green-700"
        />
        <Tarjeta
          valor={sinEmpezarNunca}
          etiqueta="No han empezado"
          tono={sinEmpezarNunca > 0 ? 'text-amber-700' : undefined}
        />
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {(
          [
            ['todos', `Todos (${filas.length})`],
            ['pendientes', `Les falta algo (${pendientes.length})`],
            ['completos', `Completaron (${completos.length})`],
          ] as const
        ).map(([clave, texto]) => (
          <button
            key={clave}
            onClick={() => onFiltro(clave)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
              filtro === clave ? 'border-blue-600 bg-blue-50 text-blue-700' : 'bg-white'
            }`}
          >
            {texto}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="w-8 px-4 py-3" />
              <th className="px-4 py-3">Recurso</th>
              <th className="px-4 py-3">DNI</th>
              <th className="px-4 py-3">Avance</th>
              <th className="px-4 py-3 text-right">Nota prom.</th>
              <th className="px-4 py-3 text-right">Puntos</th>
              <th className="px-4 py-3">Última actividad</th>
            </tr>
          </thead>
          <tbody>
            {visibles.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  {filas.length === 0
                    ? 'Nadie tiene acceso a este espacio todavía.'
                    : 'Nadie en este filtro.'}
                </td>
              </tr>
            ) : (
              visibles.map((r) => (
                <Fragment key={r.worker_id}>
                <tr
                  className="cursor-pointer border-t hover:bg-gray-50"
                  onClick={() => setAbierta(abierta === r.worker_id ? null : r.worker_id)}
                >
                  <td className="px-4 py-3 text-gray-400">
                    <button
                      type="button"
                      aria-label={abierta === r.worker_id ? 'Cerrar detalle' : 'Ver detalle'}
                      aria-expanded={abierta === r.worker_id}
                      className="text-xs"
                    >
                      {abierta === r.worker_id ? '▾' : '▸'}
                    </button>
                  </td>
                  <td className="px-4 py-3 font-medium">{r.nombre}</td>
                  <td className="px-4 py-3 text-gray-500">{r.dni}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap font-semibold">
                        {r.cursos_aprobados}/{r.cursos_publicados}
                      </span>
                      {r.en_curso > 0 && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                          {r.en_curso} a medias
                        </span>
                      )}
                      {r.sin_empezar > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
                          {r.sin_empezar} sin empezar
                        </span>
                      )}
                      {r.sin_empezar === 0 && r.en_curso === 0 && r.cursos_publicados > 0 && (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                          ✓ Completo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">{r.nota_promedio ?? '—'}</td>
                  <td className="px-4 py-3 text-right">{r.puntos}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {r.ultima_actividad
                      ? new Date(r.ultima_actividad).toLocaleDateString('es-PE', {
                          timeZone: 'America/Lima',
                        })
                      : 'Nunca entró'}
                  </td>
                </tr>
                {abierta === r.worker_id && (
                  <tr className="border-t bg-gray-50">
                    <td colSpan={7} className="px-4 py-4">
                      <DetalleDelRecurso espacioId={espacioId} workerId={r.worker_id} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Tarjeta({
  valor,
  etiqueta,
  tono = 'text-gray-900',
}: {
  valor: number;
  etiqueta: string;
  tono?: string;
}) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <p className={`text-2xl font-bold ${tono}`}>{valor}</p>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{etiqueta}</p>
    </div>
  );
}

function mensaje(err: unknown): string {
  const m = err instanceof Error ? err.message : String(err);
  // El error crudo de PostgREST no le dice nada a nadie; este sí, y apunta al arreglo.
  if (m.includes('PGRST106')) {
    return 'El esquema `academia` no está expuesto en la API del proyecto (Settings → API → Exposed schemas).';
  }
  if (m.includes('42501')) return 'No tienes permiso para esta acción.';
  return m;
}

/**
 * El detalle de una persona: qué curso tomó, con qué nota y, sobre todo, QUÉ pregunta falló.
 *
 * Se pide al abrir la fila y no junto con la tabla: son 31 personas y cada detalle cruza
 * intentos con preguntas y opciones. Traerlo todo de entrada haría lenta una pantalla que
 * casi siempre se usa para mirar el agregado.
 */
function DetalleDelRecurso({ espacioId, workerId }: { espacioId: string; workerId: string }) {
  const [cursos, setCursos] = useState<CursoDelRecurso[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setCursos(null);
    setError(null);
    obtenerDetalleRecurso(espacioId, workerId)
      .then((c) => {
        if (vivo) setCursos(c);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : 'No se pudo cargar el detalle.');
      });
    return () => {
      vivo = false;
    };
  }, [espacioId, workerId]);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (cursos === null) return <p className="text-sm text-gray-500">Cargando el detalle…</p>;
  if (cursos.length === 0) return <p className="text-sm text-gray-500">No hay cursos publicados.</p>;

  return (
    <div className="space-y-4">
      {cursos.map((c) => {
        const ultimo = c.intentos[0];
        return (
          <div key={c.curso_id} className="rounded-lg border bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="font-semibold">{c.titulo}</h4>
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">
                  Lectura {c.bloques_leidos}/{c.bloques_totales}
                </span>
                {ultimo ? (
                  <span
                    className={`rounded-full px-2 py-0.5 font-semibold ${
                      ultimo.aprobado ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-900'
                    }`}
                  >
                    Nota {ultimo.nota} · {ultimo.correctas}/{ultimo.total}
                    {ultimo.aprobado ? ' · aprobado' : ` · necesita ${c.nota_minima}`}
                  </span>
                ) : (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
                    {c.inscrito ? 'Empezado, sin rendir' : 'Sin empezar'}
                  </span>
                )}
                {c.intentos.length > 1 && (
                  <span className="text-gray-500">{c.intentos.length} intentos</span>
                )}
              </div>
            </div>

            {ultimo && (
              <ol className="mt-3 space-y-2">
                {ultimo.preguntas.map((p) => (
                  <li
                    key={p.orden}
                    className={`rounded-md border-l-4 px-3 py-2 text-sm ${
                      p.acerto ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
                    }`}
                  >
                    <p className="font-medium">
                      {p.acerto ? '✓' : '✕'} {p.orden}. {p.enunciado}
                    </p>
                    <p className="mt-1 text-gray-700">
                      <span className="text-gray-500">Marcó:</span> {p.marco ?? '—'}
                    </p>
                    {/* La correcta solo se repite cuando falló: si acertó, ya la dijo arriba. */}
                    {!p.acerto && (
                      <p className="text-gray-700">
                        <span className="text-gray-500">Era:</span> {p.correcta ?? '—'}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}

            {ultimo && c.intentos.length > 1 && (
              <p className="mt-2 text-xs text-gray-500">
                El detalle es del último intento. Anteriores:{' '}
                {c.intentos
                  .slice(1)
                  .map((i) => `${i.nota}`)
                  .join(' · ')}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
